import {
  EDITIONS,
  FIELD_BUCKETS,
  LENSES,
  PERIODS,
  PRICE_SOURCES,
  RACE_SCOPES,
  REPORT_TYPES,
} from "@/utils/performance/constants";
import { stepAnchor } from "@/utils/performance/period";

const labelCls = "block text-[11px] font-medium text-slate-400 uppercase mb-1";
const selectCls =
  "text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200";

/**
 * Report-type tabs + shared filter bar for both Performance pages.
 * Controlled entirely by `filters` / `setFilter` from usePerformanceFilters.
 */
export default function PerformanceFilterBar({
  filters,
  setFilter,
  tracks = [],
  periodLabel = "",
  rangeLabel = "",
  dataMin = "",
  dataMax = "",
  rightSlot = null,
  highlightTrack = false,
}) {
  const anchor = filters.anchor || dataMax || "";
  const stepBtn =
    "px-2 py-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent";
  const showAnchor = filters.period !== "custom";
  return (
    <>
      {/* Report type tabs */}
      <div className="flex gap-1 mb-3">
        {REPORT_TYPES.map((rt) => {
          const active = filters.type === rt.key;
          if (active) {
            return (
              <button
                key={rt.key}
                className="px-4 py-2 text-sm font-semibold rounded-t-lg bg-white text-slate-900 border border-b-0 border-slate-200 shadow-sm"
              >
                {rt.label}
              </button>
            );
          }
          return (
            <button
              key={rt.key}
              disabled={!rt.enabled}
              onClick={() => rt.enabled && setFilter({ type: rt.key })}
              title={rt.enabled ? undefined : "Coming soon"}
              className={
                rt.enabled
                  ? "px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-600"
                  : "px-4 py-2 text-sm font-medium text-slate-300 cursor-not-allowed"
              }
            >
              {rt.label}
              {!rt.enabled && (
                <span className="ml-1 text-[9px] uppercase tracking-wide text-slate-300">
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-5 flex flex-wrap items-end gap-4">
        <div>
          <label className={labelCls}>Period</label>
          <div className="flex rounded-lg overflow-hidden border border-slate-200 text-sm">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setFilter({ period: p })}
                className={
                  filters.period === p
                    ? "px-3 py-1.5 bg-emerald-600 text-white font-semibold capitalize"
                    : "px-3 py-1.5 text-slate-500 hover:bg-slate-50 capitalize"
                }
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {showAnchor && (
          <div>
            <label className={labelCls}>
              {filters.period === "daily"
                ? "Day"
                : filters.period === "monthly"
                  ? "Month"
                  : "Week of"}
            </label>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-lg overflow-hidden border border-slate-200">
                <button
                  type="button"
                  className={stepBtn}
                  onClick={() =>
                    setFilter({
                      anchor: stepAnchor(
                        anchor,
                        filters.period,
                        -1,
                        dataMin,
                        dataMax,
                      ),
                    })
                  }
                  disabled={!!dataMin && anchor <= dataMin}
                  aria-label="Previous period"
                >
                  ‹
                </button>
                <input
                  type="date"
                  value={anchor}
                  min={dataMin || undefined}
                  max={dataMax || undefined}
                  onChange={(e) =>
                    e.target.value && setFilter({ anchor: e.target.value })
                  }
                  className="text-sm px-2 py-1.5 bg-white focus:outline-none"
                />
                <button
                  type="button"
                  className={stepBtn}
                  onClick={() =>
                    setFilter({
                      anchor: stepAnchor(
                        anchor,
                        filters.period,
                        1,
                        dataMin,
                        dataMax,
                      ),
                    })
                  }
                  disabled={!!dataMax && anchor >= dataMax}
                  aria-label="Next period"
                >
                  ›
                </button>
              </div>
              {rangeLabel && (
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {rangeLabel}
                </span>
              )}
            </div>
          </div>
        )}

        {filters.period === "custom" && (
          <div className="flex items-end gap-2">
            <div>
              <label className={labelCls}>From</label>
              <input
                type="date"
                value={filters.from || ""}
                onChange={(e) => setFilter({ from: e.target.value })}
                className={selectCls}
              />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input
                type="date"
                value={filters.to || ""}
                onChange={(e) => setFilter({ to: e.target.value })}
                className={selectCls}
              />
            </div>
          </div>
        )}

        <div>
          <label className={labelCls}>Track</label>
          <select
            value={filters.track}
            onChange={(e) => setFilter({ track: e.target.value })}
            className={
              highlightTrack && filters.track !== "all"
                ? `${selectCls} border-emerald-300 ring-1 ring-emerald-200 font-medium`
                : selectCls
            }
          >
            <option value="all">All tracks</option>
            {tracks.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Tip edition</label>
          <select
            value={filters.edition}
            onChange={(e) => setFilter({ edition: e.target.value })}
            className={selectCls}
          >
            <option value="all">All editions</option>
            {EDITIONS.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Field size</label>
          <select
            value={filters.fieldSize}
            onChange={(e) => setFilter({ fieldSize: e.target.value })}
            className={selectCls}
          >
            <option value="all">All</option>
            {FIELD_BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Places lens</label>
          <select
            value={filters.lens}
            onChange={(e) => setFilter({ lens: e.target.value })}
            className={selectCls}
          >
            {LENSES.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>ROI price</label>
          <select
            value={filters.priceSource}
            onChange={(e) => setFilter({ priceSource: e.target.value })}
            className={selectCls}
          >
            {PRICE_SOURCES.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Races</label>
          <select
            value={filters.races}
            onChange={(e) => setFilter({ races: e.target.value })}
            className={selectCls}
          >
            {RACE_SCOPES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {(periodLabel || rightSlot) && (
          <div className="ml-auto flex items-center gap-3">
            {periodLabel && (
              <span className="text-xs text-slate-400">{periodLabel}</span>
            )}
            {rightSlot}
          </div>
        )}
      </div>
    </>
  );
}
